import React, { Suspense } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { PermissionsProvider } from '@/contexts/PermissionsContext';
import { UnreadMessagesProvider } from '@/contexts/UnreadMessagesContext';
import { Toaster } from 'sonner';
import SoftPhone from '@/components/SoftPhone';
import '@/i18n';
import '@/utils/axiosConfig';

// Critical pages - static import (needed immediately)
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Dashboard from '@/pages/Dashboard';
import ForceChangePassword from '@/components/ForceChangePassword';

// Lazy-loaded pages (loaded on demand)
const ForgotPassword = React.lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('@/pages/ResetPassword'));
const NotFound = React.lazy(() => import('@/pages/NotFound'));
const Compliance = React.lazy(() => import('@/pages/Compliance'));
const Groups = React.lazy(() => import('@/pages/Groups'));
const GroupDetail = React.lazy(() => import('@/pages/GroupDetail'));
const MassImport = React.lazy(() => import('@/pages/MassImport'));
const OrganizeCenter = React.lazy(() => import('@/pages/OrganizeCenter'));
const DataIntegrity = React.lazy(() => import('@/pages/DataIntegrity'));
const ProgramsAdmin = React.lazy(() => import('@/pages/ProgramsAdmin'));
const RecruitCommandCenter = React.lazy(() => import('@/pages/recruitai/RecruitCommandCenter'));
const RecruitEmployers = React.lazy(() => import('@/pages/recruitai/RecruitEmployers'));
const RecruitEmployerDetail = React.lazy(() => import('@/pages/recruitai/RecruitEmployerDetail'));
const RecruitJobs = React.lazy(() => import('@/pages/recruitai/RecruitJobs'));
const RecruitJobDetail = React.lazy(() => import('@/pages/recruitai/RecruitJobDetail'));
const RecruitCandidates = React.lazy(() => import('@/pages/recruitai/RecruitCandidates'));
const RecruitCandidateDetail = React.lazy(() => import('@/pages/recruitai/RecruitCandidateDetail'));
const RecruitImmigration = React.lazy(() => import('@/pages/recruitai/RecruitImmigration'));
const RecruitVoice = React.lazy(() => import('@/pages/recruitai/RecruitVoice'));
const RecruitDiscovery = React.lazy(() => import('@/pages/recruitai/RecruitDiscovery'));
const RecruitOutreach = React.lazy(() => import('@/pages/recruitai/RecruitOutreach'));
const RecruitBank = React.lazy(() => import('@/pages/recruitai/RecruitBank'));
const MetaAds = React.lazy(() => import('@/pages/marketing/MetaAds'));
const CandidatePublic = React.lazy(() => import('@/pages/recruitai/CandidatePublic'));
const RecruitAutopilot = React.lazy(() => import('@/pages/recruitai/RecruitAutopilot'));
const EimtBoard = React.lazy(() => import('@/pages/recruitai/EimtBoard'));
const RespondPublic = React.lazy(() => import('@/pages/recruitai/RespondPublic'));
const ContractSignPublic = React.lazy(() => import('@/pages/recruitai/ContractSignPublic'));
const EmployerPackagePublic = React.lazy(() => import('@/pages/recruitai/EmployerPackagePublic'));
const AvailabilityPublic = React.lazy(() => import('@/pages/recruitai/AvailabilityPublic'));
const PostInterviewDecisionPublic = React.lazy(() => import('@/pages/recruitai/PostInterviewDecisionPublic'));
const DocumentsUploadPublic = React.lazy(() => import('@/pages/recruitai/DocumentsUploadPublic'));
const ApplyPublic = React.lazy(() => import('@/pages/recruitai/ApplyPublic'));
const EmployerPortal = React.lazy(() => import('@/pages/recruitai/EmployerPortal'));
const CandidatePortal = React.lazy(() => import('@/pages/recruitai/CandidatePortal'));
const Files = React.lazy(() => import('@/pages/Files'));
const FileDetail = React.lazy(() => import('@/pages/FileDetail'));
const Employees = React.lazy(() => import('@/pages/Employees'));
const Clients = React.lazy(() => import('@/pages/Clients'));
const ClientsList = React.lazy(() => import('@/pages/ClientsList'));
const Messages = React.lazy(() => import('@/pages/Messages'));
const MessagingPermissions = React.lazy(() => import('@/pages/MessagingPermissions'));
const Quotes = React.lazy(() => import('@/pages/Quotes'));
const Invoices = React.lazy(() => import('@/pages/Invoices'));
const Profile = React.lazy(() => import('@/pages/Profile'));
const AuditLogs = React.lazy(() => import('@/pages/AuditLogs'));
const DocumentApprovals = React.lazy(() => import('@/pages/DocumentApprovals'));
const WorkflowTracker = React.lazy(() => import('@/pages/WorkflowTracker'));
const Timesheet = React.lazy(() => import('@/pages/Timesheet'));
const RH = React.lazy(() => import('@/pages/RH'));
const MonDossier = React.lazy(() => import('@/pages/MonDossier'));
const Payments = React.lazy(() => import('@/pages/Payments'));
const Integrations = React.lazy(() => import('@/pages/Integrations'));
const Approvals = React.lazy(() => import('@/pages/Approvals'));
const PaypalReturn = React.lazy(() => import('@/pages/PaypalReturn'));
const PublicPayRequest = React.lazy(() => import('@/pages/PublicPayRequest'));
const RoleManagement = React.lazy(() => import('@/pages/RoleManagement'));
const GestionPaie = React.lazy(() => import('@/pages/rh/GestionPaie'));
const Tresorerie = React.lazy(() => import('@/pages/Tresorerie'));
const ReconciliationCC = React.lazy(() => import('@/pages/ReconciliationCC'));
const CompteClient = React.lazy(() => import('@/pages/CompteClient'));
const Analytics = React.lazy(() => import('@/pages/Analytics'));
const Reminders = React.lazy(() => import('@/pages/Reminders'));
const Eligibility = React.lazy(() => import('@/pages/Eligibility'));
const EligibilityAssessment = React.lazy(() => import('@/pages/EligibilityAssessment'));
const Comparator = React.lazy(() => import('@/pages/Comparator'));
const DocumentChecklist = React.lazy(() => import('@/pages/DocumentChecklist'));
const EmployeeManagement = React.lazy(() => import('@/pages/EmployeeManagement'));
const KanbanBoard = React.lazy(() => import('@/pages/KanbanBoard'));
const AuditLogPage = React.lazy(() => import('@/pages/AuditLogPage'));
const JobBoard = React.lazy(() => import('@/pages/JobBoard'));
const RecruitmentAdmin = React.lazy(() => import('@/pages/RecruitmentAdmin'));
const Organizations = React.lazy(() => import('@/pages/Organizations'));
const OrgProfile = React.lazy(() => import('@/pages/OrgProfile'));
const Prospects = React.lazy(() => import('@/pages/Prospects'));
const FileTransfers = React.lazy(() => import('@/pages/FileTransfers'));
const Subscriptions = React.lazy(() => import('@/pages/Subscriptions'));
const Signatures = React.lazy(() => import('@/pages/Signatures'));
const IRCCControl = React.lazy(() => import('@/pages/IRCCControl'));
const HistoricalImport = React.lazy(() => import('@/pages/HistoricalImport'));
const DeactivatedFiles = React.lazy(() => import('@/pages/DeactivatedFiles'));
const DestructionLog = React.lazy(() => import('@/pages/DestructionLog'));
const EmailReconstruction = React.lazy(() => import('@/pages/EmailReconstruction'));
const DocumentTemplates = React.lazy(() => import('@/pages/DocumentTemplates'));
const EmailSettings = React.lazy(() => import('@/pages/EmailSettings'));
const DynamicQuestionnaires = React.lazy(() => import('@/pages/DynamicQuestionnaires'));
const RulesEngine = React.lazy(() => import('@/pages/RulesEngine'));
const WhatsAppInbox = React.lazy(() => import('@/pages/WhatsAppInbox'));
const UnifiedCalendar = React.lazy(() => import('@/pages/UnifiedCalendar'));
const ActionsRequired = React.lazy(() => import('@/pages/ActionsRequired'));
const Reporting = React.lazy(() => import('@/pages/Reporting'));
const CommunicationsCenter = React.lazy(() => import('@/pages/CommunicationsCenter'));
const AIAssistant = React.lazy(() => import('@/pages/AIAssistant'));

// Loading fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-red-600 mx-auto mb-3"></div>
      <p className="text-sm text-gray-500">Chargement...</p>
    </div>
  </div>
);

// Error Boundary for lazy-loaded components
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    this.setState({ errorInfo: info });
    try { console.error('[ErrorBoundary]', error, info?.componentStack); } catch (e) { void 0; }
    // Auto-recover from stale chunks after a redeploy (reload once)
    try {
      const msg = String(error?.message || error);
      if ((error?.name === 'ChunkLoadError' || /Loading (CSS )?chunk .* failed/i.test(msg)) && !sessionStorage.getItem('chunk_reloaded')) {
        sessionStorage.setItem('chunk_reloaded', '1');
        window.location.reload();
      }
    } catch (e) { void 0; }
  }
  render() {
    if (this.state.hasError) {
      const errMsg = this.state.error ? String(this.state.error.message || this.state.error) : 'Erreur inconnue';
      const stack = this.state.errorInfo?.componentStack || this.state.error?.stack || '';
      return (
        <div className="flex items-center justify-center min-h-[60vh]" data-testid="error-boundary">
          <div className="text-center p-8 max-w-2xl">
            <p className="text-xl font-bold text-gray-800 mb-2">Une erreur est survenue</p>
            <p className="text-gray-500 mb-4">{`La page n'a pas pu etre chargee.`}</p>
            <div className="text-left bg-red-50 border border-red-200 rounded-lg p-3 mb-4 overflow-auto max-h-64">
              <p className="text-sm font-mono text-red-700 break-all" data-testid="error-boundary-message">{errMsg}</p>
              {stack && <pre className="text-[10px] text-red-500 mt-2 whitespace-pre-wrap break-all">{stack.substring(0, 1500)}</pre>}
            </div>
            <button onClick={() => { this.setState({ hasError: false, error: null, errorInfo: null }); window.location.reload(); }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
              Recharger la page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, mustChangePassword, token, passwordChanged } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) return <Navigate to="/login" />;
  
  if (mustChangePassword) {
    return <ForceChangePassword token={token} onComplete={passwordChanged} />;
  }
  
  return children;
};

const RecruitStaffRoute = ({ children }) => {
  const { user } = useAuth();
  if (user?.role === 'employer') return <Navigate to="/portail-employeur" replace />;
  if (user?.role === 'candidate') return <Navigate to="/portail-candidat" replace />;
  if (user?.role === 'client') return <Navigate to="/dashboard" replace />;
  return children;
};

const SoftPhoneGate = () => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  if (!isAuthenticated || location.pathname.startsWith('/postuler') || location.pathname === '/candidat' || ['/reponse/', '/signer/', '/signer-offre/', '/candidats/', '/disponibilites/', '/decision/', '/documents/', '/payer/'].some(p => location.pathname.startsWith(p))) return null;
  if (user?.role === 'candidate' || user?.role === 'employer') return null;
  return <SoftPhone />;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
      </div>
    );
  }
  
  return isAuthenticated ? <Navigate to="/dashboard" /> : children;
};

function AppRoutes() {
  return (
    <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/files" element={<ProtectedRoute><Files /></ProtectedRoute>} />
      <Route path="/files/:fileId" element={<ProtectedRoute><FileDetail /></ProtectedRoute>} />
      <Route path="/groupes" element={<ProtectedRoute><Groups /></ProtectedRoute>} />
      <Route path="/groupes/:groupId" element={<ProtectedRoute><GroupDetail /></ProtectedRoute>} />
      <Route path="/employees" element={<ProtectedRoute><Employees /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
      <Route path="/clients-list" element={<ProtectedRoute><ClientsList /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/messaging-permissions" element={<ProtectedRoute><MessagingPermissions /></ProtectedRoute>} />
      <Route path="/quotes" element={<ProtectedRoute><Quotes /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/audit" element={<ProtectedRoute><AuditLogs /></ProtectedRoute>} />
      <Route path="/document-approvals" element={<ProtectedRoute><DocumentApprovals /></ProtectedRoute>} />
      <Route path="/workflow-tracker" element={<ProtectedRoute><WorkflowTracker /></ProtectedRoute>} />
      <Route path="/timesheet" element={<ProtectedRoute><Timesheet /></ProtectedRoute>} />
      <Route path="/rh" element={<ProtectedRoute><RH /></ProtectedRoute>} />
      <Route path="/mon-dossier" element={<ProtectedRoute><MonDossier /></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
      <Route path="/paiement/retour" element={<ProtectedRoute><PaypalReturn /></ProtectedRoute>} />
      <Route path="/integrations" element={<ProtectedRoute><Integrations /></ProtectedRoute>} />
      <Route path="/approbations" element={<ProtectedRoute><Approvals /></ProtectedRoute>} />
      <Route path="/payer/:token" element={<React.Suspense fallback={<div />}><PublicPayRequest /></React.Suspense>} />
      <Route path="/gestion-paie" element={<ProtectedRoute><GestionPaie /></ProtectedRoute>} />
      <Route path="/tresorerie" element={<ProtectedRoute><Tresorerie /></ProtectedRoute>} />
      <Route path="/rapprochement-cc" element={<ProtectedRoute><ReconciliationCC /></ProtectedRoute>} />
      <Route path="/import-historique" element={<ProtectedRoute><HistoricalImport /></ProtectedRoute>} />
      <Route path="/dossiers-desactives" element={<ProtectedRoute><DeactivatedFiles /></ProtectedRoute>} />
      <Route path="/registre-destructions" element={<ProtectedRoute><DestructionLog /></ProtectedRoute>} />
      <Route path="/conformite" element={<ProtectedRoute><Compliance /></ProtectedRoute>} />
      <Route path="/importation-massive" element={<ProtectedRoute><MassImport /></ProtectedRoute>} />
      <Route path="/organisation-intelligente" element={<ProtectedRoute><OrganizeCenter /></ProtectedRoute>} />
      <Route path="/integrite" element={<ProtectedRoute><DataIntegrity /></ProtectedRoute>} />
      <Route path="/gestion-programmes" element={<ProtectedRoute><ProgramsAdmin /></ProtectedRoute>} />
      <Route path="/reconstruction-email" element={<ProtectedRoute><EmailReconstruction /></ProtectedRoute>} />
      <Route path="/compte-client" element={<ProtectedRoute><CompteClient /></ProtectedRoute>} />
      <Route path="/role-management" element={<ProtectedRoute><RoleManagement /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
      <Route path="/reminders" element={<ProtectedRoute><Reminders /></ProtectedRoute>} />
      <Route path="/eligibility" element={<ProtectedRoute><Eligibility /></ProtectedRoute>} />
      <Route path="/eligibility/:id" element={<ProtectedRoute><EligibilityAssessment /></ProtectedRoute>} />
      <Route path="/comparator" element={<ProtectedRoute><Comparator /></ProtectedRoute>} />
      <Route path="/document-checklist" element={<ProtectedRoute><DocumentChecklist /></ProtectedRoute>} />
      <Route path="/employee-management" element={<ProtectedRoute><EmployeeManagement /></ProtectedRoute>} />
      <Route path="/kanban" element={<ProtectedRoute><KanbanBoard /></ProtectedRoute>} />
      <Route path="/audit-log" element={<ProtectedRoute><AuditLogPage /></ProtectedRoute>} />
      <Route path="/recrutement" element={<React.Suspense fallback={<div />}><JobBoard /></React.Suspense>} />
      <Route path="/recruitment-admin" element={<ProtectedRoute><RecruitmentAdmin /></ProtectedRoute>} />
      <Route path="/recrutement-ai" element={<ProtectedRoute><RecruitStaffRoute><RecruitCommandCenter /></RecruitStaffRoute></ProtectedRoute>} />
      <Route path="/recrutement-ai/employeurs" element={<ProtectedRoute><RecruitStaffRoute><RecruitEmployers /></RecruitStaffRoute></ProtectedRoute>} />
      <Route path="/recrutement-ai/employeurs/:employerId" element={<ProtectedRoute><RecruitStaffRoute><RecruitEmployerDetail /></RecruitStaffRoute></ProtectedRoute>} />
      <Route path="/recrutement-ai/postes" element={<ProtectedRoute><RecruitStaffRoute><RecruitJobs /></RecruitStaffRoute></ProtectedRoute>} />
      <Route path="/recrutement-ai/postes/:jobId" element={<ProtectedRoute><RecruitStaffRoute><RecruitJobDetail /></RecruitStaffRoute></ProtectedRoute>} />
      <Route path="/recrutement-ai/candidats" element={<ProtectedRoute><RecruitStaffRoute><RecruitCandidates /></RecruitStaffRoute></ProtectedRoute>} />
      <Route path="/recrutement-ai/candidats/:candidateId" element={<ProtectedRoute><RecruitStaffRoute><RecruitCandidateDetail /></RecruitStaffRoute></ProtectedRoute>} />
      <Route path="/recrutement-ai/immigration" element={<ProtectedRoute><RecruitStaffRoute><RecruitImmigration /></RecruitStaffRoute></ProtectedRoute>} />
      <Route path="/recrutement-ai/appels" element={<ProtectedRoute><RecruitStaffRoute><RecruitVoice /></RecruitStaffRoute></ProtectedRoute>} />
      <Route path="/recrutement-ai/decouverte" element={<ProtectedRoute><RecruitStaffRoute><RecruitDiscovery /></RecruitStaffRoute></ProtectedRoute>} />
      <Route path="/recrutement-ai/prospection" element={<ProtectedRoute><RecruitStaffRoute><RecruitOutreach /></RecruitStaffRoute></ProtectedRoute>} />
      <Route path="/recrutement-ai/banque" element={<ProtectedRoute><RecruitStaffRoute><RecruitBank /></RecruitStaffRoute></ProtectedRoute>} />
      <Route path="/marketing/meta-ads" element={<ProtectedRoute><RecruitStaffRoute><MetaAds /></RecruitStaffRoute></ProtectedRoute>} />
      <Route path="/candidat" element={<React.Suspense fallback={<div />}><CandidatePublic /></React.Suspense>} />
      <Route path="/recrutement-ai/autopilot" element={<ProtectedRoute><RecruitStaffRoute><RecruitAutopilot /></RecruitStaffRoute></ProtectedRoute>} />
      <Route path="/recrutement-ai/eimt" element={<ProtectedRoute><RecruitStaffRoute><EimtBoard /></RecruitStaffRoute></ProtectedRoute>} />
      <Route path="/reponse/:token" element={<React.Suspense fallback={<div />}><RespondPublic /></React.Suspense>} />
      <Route path="/signer/:token" element={<React.Suspense fallback={<div />}><ContractSignPublic /></React.Suspense>} />
      <Route path="/candidats/:token" element={<React.Suspense fallback={<div />}><EmployerPackagePublic /></React.Suspense>} />
      <Route path="/disponibilites/:token" element={<React.Suspense fallback={<div />}><AvailabilityPublic /></React.Suspense>} />
      <Route path="/signer-offre/:token" element={<React.Suspense fallback={<div />}><ContractSignPublic kind="offer" /></React.Suspense>} />
      <Route path="/decision/:token" element={<React.Suspense fallback={<div />}><PostInterviewDecisionPublic /></React.Suspense>} />
      <Route path="/documents/:token" element={<React.Suspense fallback={<div />}><DocumentsUploadPublic /></React.Suspense>} />
      <Route path="/postuler" element={<React.Suspense fallback={<div />}><ApplyPublic /></React.Suspense>} />
      <Route path="/postuler/:jobId" element={<React.Suspense fallback={<div />}><ApplyPublic /></React.Suspense>} />
      <Route path="/portail-employeur" element={<ProtectedRoute><EmployerPortal /></ProtectedRoute>} />
      <Route path="/portail-candidat" element={<ProtectedRoute><CandidatePortal /></ProtectedRoute>} />
      <Route path="/organizations" element={<ProtectedRoute><Organizations /></ProtectedRoute>} />
      <Route path="/org-profile" element={<ProtectedRoute><OrgProfile /></ProtectedRoute>} />
      <Route path="/prospects" element={<ProtectedRoute><Prospects /></ProtectedRoute>} />
      <Route path="/transfers" element={<ProtectedRoute><FileTransfers /></ProtectedRoute>} />
      <Route path="/subscriptions" element={<ProtectedRoute><Subscriptions /></ProtectedRoute>} />
      <Route path="/signatures" element={<ProtectedRoute><Signatures /></ProtectedRoute>} />
      <Route path="/ircc-control" element={<ProtectedRoute><IRCCControl /></ProtectedRoute>} />
      <Route path="/document-templates" element={<ProtectedRoute><DocumentTemplates /></ProtectedRoute>} />
      <Route path="/email-settings" element={<ProtectedRoute><EmailSettings /></ProtectedRoute>} />
      <Route path="/questionnaire-builder" element={<ProtectedRoute><DynamicQuestionnaires /></ProtectedRoute>} />
      <Route path="/rules-engine" element={<ProtectedRoute><RulesEngine /></ProtectedRoute>} />
      <Route path="/whatsapp" element={<ProtectedRoute><WhatsAppInbox /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><UnifiedCalendar /></ProtectedRoute>} />
      <Route path="/actions-required" element={<ProtectedRoute><ActionsRequired /></ProtectedRoute>} />
      <Route path="/reporting" element={<ProtectedRoute><Reporting /></ProtectedRoute>} />
      <Route path="/communications" element={<ProtectedRoute><CommunicationsCenter /></ProtectedRoute>} />
      <Route path="/ai-assistant" element={<ProtectedRoute><AIAssistant /></ProtectedRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PermissionsProvider>
            <UnreadMessagesProvider>
              <AppRoutes />
              <SoftPhoneGate />
              <Toaster position="top-right" richColors />
            </UnreadMessagesProvider>
          </PermissionsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;